import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { useWindowWidth } from '../hooks';
import {
  TEAL, AMBER, RED, BORD, CARD, SUNK, MUTED, DIM, TEXT, MOVES, RECORD_KINDS, num, qty, packs, afterMove, kindOf,
} from '../stockShared';

// Record one movement of stock (the stock module, 22 Oct 2026): find the
// item, tap what happened, type how many. On a phone it rises from the
// bottom with big buttons; on a desktop it sits in the middle.
//
// item: recorded for this one (from its row or its page), or null to pick
// items: every item, to pick from   onSaved(levels): after saving

export default function StockRecord({ item: given = null, items = [], onClose, onSaved, startKind = 'used' }) {
  const phone = useWindowWidth() < 640;
  // Which of the director's switches are on (Stock → Automatic stock usage, 085): they
  // add Pallet packed, and Sent out / Back for a step done outside.
  const [sw, setSw] = useState({});
  useEffect(() => {
    api.get('/api/v1/stock/auto').then(r => setSw(Object.fromEntries((r.data.switches || []).map(s => [s.key, s.on])))).catch(() => setSw({}));
  }, []);
  const [item, setItem] = useState(given);
  const [find, setFind] = useState('');
  const [kind, setKind] = useState(startKind);
  const [chamfered, setChamfered] = useState(false);
  const [amount, setAmount] = useState('');
  const [inPacks, setInPacks] = useState(false);
  const [more, setMore] = useState(false);
  const [note, setNote] = useState('');
  const [lot, setLot] = useState('');
  const [at, setAt] = useState('');            // '' = now
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [consume, setConsume] = useState(true);  // Made: take what it is made from off stock
  const qtyRef = useRef(null);

  useEffect(() => { if (item && qtyRef.current) qtyRef.current.focus(); }, [item, kind]);

  const found = useMemo(() => {
    const q = find.trim().toLowerCase();
    const list = items.filter(i => i.active !== false);
    return (q ? list.filter(i => i.name.toLowerCase().includes(q) || (i.link?.name || '').toLowerCase().includes(q)) : list).slice(0, 60);
  }, [find, items]);

  const packQty = item?.pack_unit && Number(item.pack_qty) > 0 ? Number(item.pack_qty) : null;
  const n = amount === '' ? null : Number(String(amount).replace(',', '.'));
  const units = n == null || !Number.isFinite(n) ? null : (inPacks && packQty ? n * packQty : n);
  // A hard tube has no button of its own for the chamfering step (the
  // director, 23 Oct 2026: "we can lose the button as we have the toggle"):
  // it is bought in, so Made with the toggle on Pofrezkane is the step -- the
  // pieces come from not pofrezkane. Made with it off means nothing.
  const step = item?.chamfer && kind === 'made';
  const sendKind = step ? (chamfered ? 'chamfered' : null) : kind;
  // Pallet packed takes a pallet's packing off; the item itself is unchanged.
  const isPallet = kind === 'pallet';
  const move = { kind: sendKind || kind, qty: units, chamfered: item?.chamfer && sendKind !== 'chamfered' ? chamfered : null };
  const level = item?.level || { total: 0, raw: 0, done: 0 };
  const after = item && units != null && sendKind && !isPallet ? afterMove(level, move, item.chamfer) : null;
  const side = (lv) => (item?.chamfer ? (sendKind === 'chamfered' ? lv.raw : (chamfered ? lv.done : lv.raw)) : lv.total);
  const before = item ? side(level) : null;
  const afterSide = after ? side(after) : null;
  const short = after && kind !== 'counted' && kind !== 'sent_out' && afterSide < 0;
  // Which of a hard tube's two stocks the line is about; the step itself takes
  // from "not pofrezkane", so that is the side that can run short.
  const sideWord = item?.chamfer && sendKind !== 'chamfered' ? (chamfered ? ' pofrezkane' : ' not pofrezkane') : '';
  const shortWord = item?.chamfer && sendKind === 'chamfered' ? ' not pofrezkane' : sideWord;

  async function save() {
    if (!item || units == null || !sendKind) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = isPallet
        ? await api.post('/api/v1/stock/auto/pallets', { item_id: item.id, pallets: units })
        : kind === 'sent_out' || kind === 'back'
          ? await api.post('/api/v1/stock/auto/outside', { item_id: item.id, kind, qty: units, note })
          : await api.post('/api/v1/stock/movements', { movements: [{
            item_id: item.id, kind: sendKind, qty: units, chamfered: move.chamfered, note, lot, consume: takes.length > 0 && consume,
            happened_at: at ? new Date(at).toISOString() : undefined,
          }] });
      onSaved?.(data.levels, { item, kind: sendKind, qty: units }, data.ids || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Not saved. Check the connection and try again.');
      setSaving(false);
    }
  }

  // Made, for an item with its inputs set (23 Oct 2026): what it takes off.
  const takes = !item?.chamfer && (kind === 'made' || kind === 'sent_out') && (item?.inputs || []).length && units != null && units > 0
    ? item.inputs.map(e => {
      const from = e.chamfer ? Number(e.level?.done) || 0 : Number(e.level?.total) || 0;
      const need = Math.round(units * Number(e.qty) * 1000) / 1000;
      return { ...e, need, from, to: Math.round((from - need) * 1000) / 1000 };
    }) : [];
  const palletable = item && (item.kind === 'finished' || (item.kind === 'moulded' && item.from_production === false));
  const outside = item && item.done_where === 'outside';
  const kinds = [...RECORD_KINDS, ...(sw.pallets && palletable ? ['pallet'] : []), ...(sw.outside && outside ? ['sent_out', 'back'] : [])];
  const canSave = !saving && units != null && !!sendKind && !(units === 0 && kind !== 'counted') && !(isPallet && !Number.isInteger(units));
  const field = { background: SUNK, border: `1px solid ${BORD}`, borderRadius: 10, color: TEXT, padding: '10px 12px', fontSize: 15, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'system-ui', colorScheme: 'dark' };
  const seg = (on, color = TEAL) => ({ flex: 1, minHeight: 44, borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui',
    background: on ? `${color}26` : SUNK, border: `1px solid ${on ? color : BORD}`, color: on ? color : DIM });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1300, display: 'flex',
      alignItems: phone ? 'flex-end' : 'center', justifyContent: 'center', padding: phone ? 0 : 16 }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label="Record stock"
        style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: phone ? '16px 16px 0 0' : 16, width: '100%', maxWidth: phone ? '100%' : 460,
                 maxHeight: phone ? '92vh' : '90vh', overflowY: 'auto', padding: phone ? '14px 16px calc(18px + env(safe-area-inset-bottom, 0px))' : '18px 20px',
                 fontFamily: 'system-ui', color: TEXT, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: MUTED, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Record</div>
            {item && (
              <div style={{ fontSize: 17, fontWeight: 800, marginTop: 2, lineHeight: 1.3 }}>
                {item.name}
                {!given && <button type="button" onClick={() => { setItem(null); setAmount(''); }} style={{ marginLeft: 8, background: 'none', border: 'none', color: TEAL, fontSize: 12.5, cursor: 'pointer', padding: 0 }}>change</button>}
              </div>
            )}
            {item && (
              <div style={{ fontSize: 12.5, color: DIM, marginTop: 2 }}>
                On hand {qty(level.total, item)}
                {item.chamfer && <> · {num(level.done)} pofrezkane · {num(level.raw)} not</>}
                {!level.counted && <span style={{ color: AMBER }}> · not counted yet</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: MUTED, fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        {!item && (
          <>
            <input autoFocus value={find} onChange={e => setFind(e.target.value)} placeholder="Find the item: Cevka, Zlozenke, Trojcek…" style={field} aria-label="Find the item" />
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '55vh', overflowY: 'auto' }}>
              {found.map(i => (
                <button key={i.id} type="button" onClick={() => setItem(i)}
                  style={{ textAlign: 'left', background: SUNK, border: `1px solid ${BORD}`, borderRadius: 10, padding: '10px 12px', cursor: 'pointer', color: TEXT, fontFamily: 'system-ui', display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{i.name}</span>
                    <span style={{ fontSize: 11.5, color: kindOf(i.kind).color }}>{kindOf(i.kind).label}</span>
                  </span>
                  <span style={{ fontSize: 13, color: DIM, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{qty(i.level.total, i)}</span>
                </button>
              ))}
              {!found.length && <div style={{ color: MUTED, fontSize: 13, padding: 8 }}>Nothing called that. Try part of the name.</div>}
            </div>
          </>
        )}

        {item && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: phone ? 4 : 6 }}>
              {/* Icon above, word below, on every one alike (the director, 27 Oct 2026). */}
              {kinds.map(k => (
                <button key={k} type="button" onClick={() => setKind(k)}
                  style={{ ...seg(kind === k, MOVES[k].color), display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, minHeight: 60, padding: '8px 4px' }}>
                  <i className={`fa-solid ${MOVES[k].icon}`} style={{ fontSize: 16 }} />
                  <span style={{ fontSize: phone ? 11.5 : 13, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{MOVES[k].label}</span>
                </button>
              ))}
            </div>

            {item.chamfer && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button type="button" onClick={() => setChamfered(false)} style={seg(!chamfered)}>Not pofrezkane</button>
                <button type="button" onClick={() => setChamfered(true)} style={seg(chamfered)}>Pofrezkane</button>
              </div>
            )}
            {step && chamfered && <div style={{ fontSize: 12.5, color: DIM, marginTop: 8, lineHeight: 1.5 }}>Made pofrezkane: the edges were done, so the pieces move from not pofrezkane to pofrezkane.</div>}
            {step && !chamfered && <div style={{ fontSize: 12.5, color: AMBER, marginTop: 8, lineHeight: 1.5 }}>A hard tube is bought in, so Made means made pofrezkane. Switch to Pofrezkane to record the edges being done.</div>}

            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', marginTop: 10 }}>
              <input ref={qtyRef} value={amount} onChange={e => setAmount(e.target.value)} inputMode="decimal" placeholder={kind === 'counted' ? 'How many are there?' : isPallet ? 'How many pallets?' : 'How many?'}
                onKeyDown={e => { if (e.key === 'Enter') save(); }}
                aria-label="How many" style={{ ...field, fontSize: 24, fontWeight: 800, padding: '10px 14px', fontVariantNumeric: 'tabular-nums' }} />
              {isPallet ? (
                <div style={{ alignSelf: 'center', color: DIM, fontSize: 15, minWidth: 30 }}>pallets</div>
              ) : packQty ? (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button type="button" onClick={() => setInPacks(false)} style={{ ...seg(!inPacks), flex: 'none', padding: '0 12px' }}>{item.unit}</button>
                  <button type="button" onClick={() => setInPacks(true)} style={{ ...seg(inPacks), flex: 'none', padding: '0 12px' }}>{item.pack_unit}</button>
                </div>
              ) : (
                <div style={{ alignSelf: 'center', color: DIM, fontSize: 15, minWidth: 30 }}>{item.unit}</div>
              )}
            </div>

            {after && (
              <div style={{ fontSize: 13, color: DIM, marginTop: 8, fontVariantNumeric: 'tabular-nums' }}>
                {inPacks && packQty ? <>{num(n)} {item.pack_unit} = {qty(units, item)} · </> : null}
                {kind === 'counted'
                  ? <>Was {qty(before, item)}{sideWord}, now <b style={{ color: TEXT }}>{qty(afterSide, item)}</b>{before !== afterSide ? ` (${afterSide - before > 0 ? '+' : ''}${num(afterSide - before)})` : ''}</>
                  : sendKind === 'chamfered'
                    ? <>Not pofrezkane {num(level.raw)} → {num(after.raw)} · pofrezkane {num(level.done)} → <b style={{ color: TEXT }}>{num(after.done)}</b></>
                    : <>{qty(before, item)}{sideWord} → <b style={{ color: TEXT }}>{qty(afterSide, item)}</b></>}
                {packs(after.total, item) && sendKind !== 'chamfered' ? ` (${packs(after.total, item)})` : ''}
              </div>
            )}
            {isPallet && (
              <div style={{ fontSize: 12.5, color: DIM, marginTop: 8, lineHeight: 1.5 }}>
                All of {item.name} for these pallets is on them, wrapped, covered and labelled: the pallet, its film, cover sheet and tape, and its label come off stock, as Admin → Packaging works them out. The pallets then wait for their lorry.
              </div>
            )}
            {kind === 'sent_out' && (
              <div style={{ fontSize: 12.5, color: DIM, marginTop: 8, lineHeight: 1.5 }}>
                Sent to {item.done_by || 'the outside company'}: what it is made from comes off the shelf, and these show as at {item.done_by || 'the company'} until they come back.{level.away > 0 ? ` ${num(level.away)} there now.` : ''}
              </div>
            )}
            {kind === 'back' && (
              <div style={{ fontSize: 12.5, color: level.away > 0 && units > level.away ? AMBER : DIM, marginTop: 8, lineHeight: 1.5 }}>
                Back from {item.done_by || 'the outside company'}, made: they go onto the shelf. {level.away > 0 ? `${num(level.away)} were there.` : 'None were recorded as sent out.'}
              </div>
            )}
            {takes.length > 0 && kind === 'sent_out' && (
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12.5, color: DIM, fontVariantNumeric: 'tabular-nums' }}>
                {takes.map(t => (
                  <div key={t.input_id} style={{ color: t.to < 0 ? AMBER : DIM }}>{num(t.need)} × {t.name}: {num(t.from)} → <b style={{ color: t.to < 0 ? AMBER : TEXT }}>{num(t.to)}</b></div>
                ))}
              </div>
            )}
            {takes.length > 0 && kind === 'made' && (
              <div style={{ marginTop: 10, background: SUNK, border: `1px solid ${BORD}`, borderRadius: 10, padding: '9px 11px' }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
                  <input type="checkbox" checked={consume} onChange={e => setConsume(e.target.checked)} style={{ width: 18, height: 18, accentColor: TEAL }} />
                  Take what it is made from off stock
                </label>
                {consume && (
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12.5, color: DIM, fontVariantNumeric: 'tabular-nums' }}>
                    {takes.map(t => (
                      <div key={t.input_id} style={{ color: t.to < 0 ? AMBER : DIM }}>
                        {num(t.need)} × {t.name}{t.chamfer ? ' (pofrezkane)' : ''}: {num(t.from)} → <b style={{ color: t.to < 0 ? AMBER : TEXT }}>{num(t.to)}</b>{t.to < 0 ? ' · below zero until the next count' : ''}
                      </div>
                    ))}
                  </div>
                )}
                {!consume && <div style={{ fontSize: 12.5, color: MUTED, marginTop: 4 }}>Only {item.name} goes up; nothing else changes. Use this where the parts were already taken off.</div>}
              </div>
            )}

            {short && (
              <div style={{ fontSize: 12.5, color: AMBER, marginTop: 6, lineHeight: 1.5 }}>
                That is more than the {qty(before, item)}{shortWord} on hand, so it will show below zero until the next count. If the figure is wrong, record a count instead.
              </div>
            )}

            <button type="button" onClick={() => setMore(m => !m)} style={{ background: 'none', border: 'none', color: TEAL, fontSize: 13, cursor: 'pointer', padding: '10px 0 0', fontFamily: 'system-ui' }}>
              <i className={`fa-solid fa-chevron-${more ? 'down' : 'right'}`} style={{ fontSize: 10, marginRight: 6 }} />Note, batch number, earlier time
            </button>
            {more && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional): what for, from which delivery…" style={field} aria-label="Note" />
                <input value={lot} onChange={e => setLot(e.target.value)} placeholder="Batch number (optional)" style={field} aria-label="Batch number" />
                <label style={{ fontSize: 12, color: DIM }}>
                  When it happened, if not now
                  <input type="datetime-local" value={at} onChange={e => setAt(e.target.value)} style={{ ...field, marginTop: 4 }} />
                </label>
              </div>
            )}

            {error && <div style={{ color: RED, fontSize: 13, marginTop: 10, lineHeight: 1.5 }}>{error}</div>}

            {/* Greyed until there is something to save (the director, 23 Oct
                2026: the old label "Type how many" read like a button that did
                nothing). */}
            <button type="button" onClick={save} disabled={!canSave}
              style={{ marginTop: 14, width: '100%', minHeight: 50, borderRadius: 12, fontSize: 16, fontWeight: 800, fontFamily: 'system-ui',
                       cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave || saving ? 1 : 0.45,
                       background: canSave ? MOVES[kind].color : 'transparent', color: canSave ? '#0f172a' : DIM, border: canSave ? 'none' : `1px dashed ${BORD}` }}>
              {saving ? 'Saving…' : !canSave ? 'Save'
                : isPallet ? `Save: ${num(units)} pallet${units === 1 ? '' : 's'} packed`
                : `Save: ${sendKind === 'chamfered' ? 'made pofrezkane' : MOVES[kind].label.toLowerCase()} ${qty(units, item)}${sideWord}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
